import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { collection, query, getDocs, updateDoc, doc, setDoc } from "firebase/firestore";
import Toast from "../components/Toast";
import "../styles/messageTemplates.css";

function MessageTemplates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState(() => {
    // Initialize with default templates
    const initialTemplates = {};
    ["invitation", "reminder", "thankyou"].forEach(type => {
      initialTemplates[type] = {
        title: defaultTemplates[type].title,
        description: defaultTemplates[type].description,
        Groom: { content: defaultTemplates[type].defaultContent, docId: null },
        Bride: { content: defaultTemplates[type].defaultContent, docId: null },
      };
    });
    return initialTemplates;
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [selectedRole, setSelectedRole] = useState("Groom");
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const messageTypes = ["invitation", "reminder", "thankyou"];
  const roles = ["Groom", "Bride"];

  const defaultTemplates = {
    invitation: {
      title: "Invitation Message",
      description: "Sent when sending initial invitation",
      defaultContent: `Dear {GUEST_NAME},

You're invited to the wedding of

💍 Chanchala & Kalana 💍

Number of Guests: {GUEST_COUNT}
Sunday, July 12, 2026

👇 View Invitation:
{INVITATION_LINK}`
    },
    reminder: {
      title: "Reminder Message",
      description: "Sent as a reminder about the wedding",
      defaultContent: `Hi {GUEST_NAME}! 📣

This is a friendly reminder about the wedding invitation.

💍 Chanchala & Kalana's Wedding 💍

Date: Sunday, July 12, 2026
Guests: {GUEST_COUNT}

👉 Click here to RSVP:
{INVITATION_LINK}`
    },
    thankyou: {
      title: "Thank You Message",
      description: "Sent after the event as thank you",
      defaultContent: `Hi {GUEST_NAME},

Thank you so much for celebrating with us! 💕

We appreciate you joining us for our special day.

See you soon!

💍 Chanchala & Kalana 💍`
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, "messageTemplates"));
      const snapshot = await getDocs(q);
      
      const templatesData = { ...templates }; // Start with current templates
      
      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.type && defaultTemplates[data.type]) {
          templatesData[data.type] = {
            ...templatesData[data.type],
            ...data,
            docId: doc.id,
          };
        }
      });

      setTemplates(templatesData);
    } catch (error) {
      console.error("Error loading templates:", error);
      showToast("Error loading templates", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async (type) => {
    try {
      setSaving(true);
      const template = templates[type];
      const roleContent = template[selectedRole]?.content || "";
      
      if (!roleContent.trim()) {
        showToast(`Message for ${selectedRole} cannot be empty`, "error");
        return;
      }

      const templateData = {
        type,
        title: template.title,
        description: template.description,
        updatedAt: new Date().toISOString(),
      };

      // Merge with existing role data
      templateData[selectedRole] = {
        content: roleContent,
        updatedAt: new Date().toISOString(),
      };

      const docRef = doc(db, "messageTemplates", type);
      await setDoc(docRef, templateData, { merge: true });

      showToast(`"${type}" template saved for ${selectedRole}!`, "success");
      // Auto-close editing after 500ms
      setTimeout(() => {
        setEditingType(null);
        loadTemplates();
      }, 500);
    } catch (error) {
      console.error("Error saving template:", error);
      showToast("Error saving template", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = (type) => {
    if (window.confirm(`Reset ${selectedRole}'s ${type} template to default?`)) {
      setTemplates({
        ...templates,
        [type]: {
          ...templates[type],
          [selectedRole]: { content: "" }
        }
      });
    }
  };

  const handleUpdateTemplate = (type, newContent) => {
    // Check if any protected variable was removed
    const protectedVars = ['{GUEST_NAME}', '{GUEST_COUNT}', '{INVITATION_LINK}'];
    const oldContent = templates[type][selectedRole]?.content || '';
    
    for (let variable of protectedVars) {
      if (oldContent.includes(variable) && !newContent.includes(variable)) {
        showToast(`Variable "${variable}" cannot be deleted!`, "error");
        return;
      }
    }

    setTemplates({
      ...templates,
      [type]: {
        ...templates[type],
        [selectedRole]: {
          ...templates[type][selectedRole],
          content: newContent
        }
      }
    });
  };

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "success" }), 3000);
  };

  if (loading) {
    return (
      <div className="templates-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="templates-container">
      <div className="templates-header">
        <button className="back-btn" onClick={() => navigate("/guests")}>
          ← Back to Guests
        </button>
        <h1>📧 Message Templates</h1>
        <p className="header-subtitle">Edit messages for Bride and Groom separately</p>
      </div>

      {/* Role Selector */}
      <div className="role-selector">
        {roles.map(role => (
          <button
            key={role}
            className={`role-tab ${selectedRole === role ? 'active' : ''}`}
            onClick={() => {
              setSelectedRole(role);
              setEditingType(null);
            }}
          >
            {role === "Groom" ? "🤵" : "👰"} {role}
          </button>
        ))}
      </div>

      <div className="templates-grid">
        {messageTypes.map(type => {
          const template = templates[type];
          const roleTemplate = template[selectedRole] || { content: "" };
          const otherRole = selectedRole === "Groom" ? "Bride" : "Groom";
          const otherRoleTemplate = template[otherRole] || { content: "" };
          const isEditing = editingType === type;

          return (
            <div key={type} className="template-card">
              <div className="template-header">
                <h3>{template.title}</h3>
                <p className="template-description">{template.description}</p>
                <p className="template-role">For {selectedRole}</p>
              </div>

              <div className="template-columns">
                {/* Your Role's Template */}
                <div className="template-column">
                  <h4 style={{ color: '#C41E3A', marginBottom: '8px' }}>Your {selectedRole}'s Message:</h4>
                  <div className="template-content">
                    {isEditing ? (
                      <textarea
                        value={roleTemplate.content}
                        onChange={(e) => handleUpdateTemplate(type, e.target.value)}
                        className="template-textarea"
                        placeholder="Edit message template..."
                        style={{ borderColor: '#C41E3A' }}
                      />
                    ) : (
                      <div className="template-preview">
                        {roleTemplate.content ? (
                          roleTemplate.content.split('\n').map((line, idx) => (
                            <p key={idx}>{line}</p>
                          ))
                        ) : (
                          <p style={{ color: '#999', fontStyle: 'italic' }}>No message set for {selectedRole}</p>
                        )}
                      </div>
                    )}
                    <div className="char-count">{roleTemplate.content.length} characters</div>
                  </div>
                </div>

                {/* Other Role's Template */}
                <div className="template-column">
                  <h4 style={{ color: '#999', marginBottom: '8px' }}>{otherRole}'s Message:</h4>
                  <div className="template-content">
                    <div className="template-preview read-only">
                      {otherRoleTemplate.content ? (
                        otherRoleTemplate.content.split('\n').map((line, idx) => (
                          <p key={idx}>{line}</p>
                        ))
                      ) : (
                        <p style={{ color: '#999', fontStyle: 'italic' }}>No message set for {otherRole}</p>
                      )}
                    </div>
                    <div className="char-count">{otherRoleTemplate.content.length} characters</div>
                  </div>
                </div>
              </div>

              <div className="template-vars">
                <p className="vars-title">Available Variables (Cannot be deleted):</p>
                <div className="vars-list">
                  <span className="var-tag">{'{'} GUEST_NAME {'}'}</span>
                  <span className="var-tag">{'{'} GUEST_COUNT {'}'}</span>
                  <span className="var-tag">{'{'} INVITATION_LINK {'}'}</span>
                </div>
              </div>

              <div className="template-actions">
                {isEditing ? (
                  <>
                    <button
                      className="save-btn"
                      onClick={() => handleSaveTemplate(type)}
                      disabled={saving}
                    >
                      {saving ? "Saving..." : "✓ Save"}
                    </button>
                    <button
                      className="cancel-btn"
                      onClick={() => {
                        setEditingType(null);
                        loadTemplates();
                      }}
                    >
                      ✕ Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="edit-btn"
                      onClick={() => setEditingType(type)}
                    >
                      ✎ Edit
                    </button>
                    <button
                      className="reset-btn"
                      onClick={() => handleResetToDefault(type)}
                    >
                      ↻ Clear
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: "", type: "success" })}
        />
      )}
    </div>
  );
}

export default MessageTemplates;
